import { useLocation } from "@reach/router"
import classNames from "classnames"
import { useStaticQuery, graphql } from "gatsby"
import { SbGatsbyStory, storyblokEditable } from "gatsby-source-storyblok"

import ToastNotification from "./V2/Banners/ToastNotification"
import Navigation from "./V2/Navigation"
import SiteWidePopup from "./V2/Popups"
import { GetStartedModal } from "./V2/Sections/GetStarted/Modal"
import "aos/dist/aos.css"
import "./layout.css"
import { mapContentHighlight } from "../utils/V2/contentHighlight"
import { mapTopNavigationMainLinks } from "../utils/V2/storyblok"

import { Color } from "@constants/V2/color"
import useIsomorphicLayoutEffect from "@hooks/V2/useIsomorphicLayoutEffect"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import { storeQueryString } from "@utils/url"
import { getBackgroundColorClass } from "@utils/V2/color"
import React, {
  lazy,
  useEffect,
  startTransition,
  useState,
  Suspense,
} from "react"

interface Props {
  children: any
}
interface QueryResult {
  footer: Storyblok.Page & SbGatsbyStory
  siteWideBanner: Storyblok.Page & SbGatsbyStory
  siteWidePopup: Storyblok.Page & SbGatsbyStory
  getStarted: Storyblok.Page & SbGatsbyStory
}

type ToastBannerProps = {
  content: Array<
    | Storyblok.ToastNotificationIconBanner
    | Storyblok.ToastNotificationCountDownBanner
  >
}

/**
 * Directories that should not have a site wide banner
 */
const EXCLUDED_SITEWIDEBANNER_PATH_PREFIXES = [
  "/za/internal/tests/",
  "/za/shop/options/",
]

export const ToastBanner = ({ content }: ToastBannerProps) => {
  return content.map((toastItem, index) => {
    if (toastItem.component === "toastNotificationCountDownBanner") {
      const item = toastItem as Storyblok.ToastNotificationCountDownBanner

      return (
        <ToastNotification
          // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
          {...storyblokEditable(toastItem)}
          key={`${item.component}-${item.text}-${index}`}
          text={item.text}
          ctaText={item.ctaText}
          ctaColor={item.ctaColor}
          textColor={item.textColor}
          component={item.component}
          timerLabel={item.timerLabel}
          ctaTextColor={item.ctaTextColor}
          timerTextColor={item.timerTextColor}
          timerTargetDate={item.timerTargetDate}
          backgroundColor={item.backgroundColor}
          secondaryBackgroundColor={item.secondaryBackgroundColor}
          ctaLink={storyblokLinkToYocoLink(item.ctaLink)}
          isSiteWideBanner={item.isSiteWideBanner}
          isAnimated={item.isAnimated}
        />
      )
    } else {
      const item = toastItem as Storyblok.ToastNotificationIconBanner
      return (
        <ToastNotification
          // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
          {...storyblokEditable(toastItem)}
          key={`${item.component}-${item.text}-${index}`}
          text={item.text}
          mobileText={item.mobileText}
          ctaText={item.ctaText}
          iconAlt={item.icon?.alt}
          ctaColor={item.ctaColor}
          icon={item.icon?.filename}
          textColor={item.textColor}
          textAlignment={item.textAlignment}
          component={item.component}
          backgroundColor={item.backgroundColor}
          secondaryBackgroundColor={item.secondaryBackgroundColor}
          ctaLink={storyblokLinkToYocoLink(item.ctaLink)}
          isSiteWideBanner={item.isSiteWideBanner}
          isAnimated={item.isAnimated}
        />
      )
    }
  })
}

/**
 * @todo we cant set this type in `Storyblok.SiteWideBanner` directly
 * because namespaces dont let us import the SbGatsbyStory type
 */
type SiteWideBannerWithExcludedPages = Storyblok.SiteWideBanner & {
  excludedPages: (SbGatsbyStory & Storyblok.Page)[]
}

type SiteWidePopupWithExcludedPages = Storyblok.SiteWidePopup & {
  excludedPages: (SbGatsbyStory & Storyblok.Page)[]
}

const Layout = ({ children }: Props) => {
  // const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(false)
  const location = useLocation()
  const Footer = lazy(() => import("./Footer"))

  const [showFooter, setShowFooter] = useState(false)

  useEffect(() => {
    document.documentElement.dataset.hydrated = "true"

    // Delay rendering the footer to ensure the children have loaded
    startTransition(() => {
      setShowFooter(true)
    })
  }, [])

  const queryResult: QueryResult = useStaticQuery(graphql`
    query StaticData {
      footer {
        uuid
        full_slug
        content
      }

      siteWideBanner {
        uuid
        full_slug
        content
      }

      siteWidePopup {
        uuid
        full_slug
        content
      }

      getStarted {
        uuid
        full_slug
        content
      }
    }
  `)

  const toastNotificationContent: (
    | Storyblok.ToastNotificationIconBanner
    | Storyblok.ToastNotificationCountDownBanner
  )[] = []

  /*
    A site wide banner can be set from config
  */
  let siteWideBannerData: SiteWideBannerWithExcludedPages

  siteWideBannerData = queryResult?.siteWideBanner
    ?.content as SiteWideBannerWithExcludedPages

  // ToDo find out why this doesnt come back parsed from graphql query
  if (typeof siteWideBannerData === "string") {
    siteWideBannerData = JSON.parse(siteWideBannerData)
  }

  const excludedSiteWideBannerPages = siteWideBannerData?.excludedPages?.map(
    (page) => `/${page.full_slug}/`
  )

  let siteWidePopupData: SiteWidePopupWithExcludedPages

  siteWidePopupData = queryResult?.siteWidePopup
    ?.content as SiteWidePopupWithExcludedPages

  if (typeof siteWidePopupData === "string") {
    siteWidePopupData = JSON.parse(siteWidePopupData)
  }

  const excludedSiteWidePopupPages =
    siteWidePopupData?.excludedPages?.map((page) => `/${page.full_slug}/`) ?? []

  let getStartedData: Storyblok.GetStartedData

  getStartedData = queryResult?.getStarted?.content as Storyblok.GetStartedData

  if (typeof getStartedData === "string") {
    getStartedData = JSON.parse(getStartedData)
  }

  /**
   * @param currentPath Always expects a path with a leading and trailing slash.
   * Example: `/za/internal/tests/`
   */
  const hideSitewideBanner = (currentPath: string): boolean => {
    return (
      EXCLUDED_SITEWIDEBANNER_PATH_PREFIXES.some((excluded_path) =>
        currentPath.startsWith(excluded_path)
      ) ||
      excludedSiteWideBannerPages.some(
        (excluded_page) => currentPath === excluded_page
      )
    )
  }

  /**
   * @param currentPath Always expects a path with a leading and trailing slash.
   * Example: `/za/internal/tests/`
   */
  const hideSitewidePopup = (currentPath: string): boolean => {
    return (
      EXCLUDED_SITEWIDEBANNER_PATH_PREFIXES.some((excluded_path) =>
        currentPath.startsWith(excluded_path)
      ) ||
      excludedSiteWidePopupPages.some(
        (excluded_page) => currentPath === excluded_page
      )
    )
  }

  /**
   * Extract the path parameter from location.search
   * @param searchString The location.search string
   * @returns The path value or empty string if not found
   */
  const getPathFromSearch = (searchString: string): string => {
    const urlParams = new URLSearchParams(searchString)
    const path = urlParams.get("path")
    if (!path) return ""

    // Remove any leading or trailing slashes and ensure exactly one of each
    const cleanPath = path.replace(/^\/+|\/+$/g, "")
    return `/${cleanPath}/`
  }

  if (
    // ToDo clean up these two location checks into one
    !hideSitewideBanner(location.pathname) &&
    !hideSitewideBanner(getPathFromSearch(location.search)) &&
    siteWideBannerData?.content?.length &&
    siteWideBannerData?.isEnabled === true
  ) {
    // Global banners always live at the top
    for (const content of siteWideBannerData.content) {
      content.isSiteWideBanner = true
    }

    toastNotificationContent.push(...siteWideBannerData.content)
  }

  const showSiteWidePopup =
    !location.search.includes("settings/site-wide-popup") &&
    !hideSitewidePopup(location.pathname) &&
    !hideSitewidePopup(getPathFromSearch(location.search)) &&
    siteWidePopupData?.isEnabled === true &&
    !!siteWidePopupData?.content?.length

  /* NOTE: A page can return navigation as a part of its story content or
     a page query can include a query of the default site header. The following
     logic prioritises custom navigation set from a page's story, then checks to
     see if a query of the default header was added, and finally sets headerData
     to undefined if no header information can be found. The header will not
     render in this case.
   */
  const story = children?.[children.length - 1]?.props?.data?.story

  let pageData = story?.content as Storyblok.PageContent
  if (typeof pageData === "string") {
    pageData = JSON.parse(pageData)
  }

  if (pageData?.toastNotificationContent?.length) {
    // Local, page-specific banners, live below any global banners
    toastNotificationContent.push(...(pageData?.toastNotificationContent ?? []))
  }
  // This is to cater for the 404 page query
  const mode = pageData?.navigationMode ? pageData.navigationMode : "dark"

  const headerContent: { content: Storyblok.TopNavigation } | undefined =
    pageData
      ? pageData.navigation
      : // This is to cater for the 404 page query
        story?.navigation?.content
        ? { content: JSON.parse(story.navigation.content) }
        : undefined

  storeQueryString(location.search)

  useIsomorphicLayoutEffect(() => {
    import("aos").then(({ default: AOS }) => {
      AOS.init({ once: true, offset: 120, duration: 500, easing: "ease" })
    })
  }, [])

  return (
    <>
      {/* 
        For /editor route:
          This will only render the siteWideBanner when not on the /settings/site-wide-banner/* story.
          This is done to prevent the site-wide-banner from rendering twice and breaking the 
          editor ui when on this path (ie: `editor/?path=settings/site-wide-banner/za`).

          See editor.tsx for rendering implementation of banners for the /editor route.

        For NON-/editor route: 
          This will render both siteWideBanner and any page banner
      */}
      {!location.search.includes("settings/site-wide-banner") &&
      toastNotificationContent.length ? (
        <ToastBanner content={toastNotificationContent} />
      ) : null}
      {showSiteWidePopup && siteWidePopupData.content ? (
        <SiteWidePopup
          blok={siteWidePopupData.content[0]}
          id={siteWidePopupData.id}
          timeToShow={siteWidePopupData.timeToShow}
        />
      ) : null}
      {headerContent ? (
        <div id="navigation">
          <Navigation
            contentHighlight={mapContentHighlight(
              headerContent.content.contentHighlight
            )}
            mode={mode}
            mainLinks={mapTopNavigationMainLinks(
              (headerContent.content as Storyblok.TopNavigation).mainLinks
            )}
            primaryButtonText={headerContent.content.primaryCTAText}
            primaryButtonLink={storyblokLinkToYocoLink(
              headerContent.content.primaryCTALink
            )}
            secondaryButtonText={headerContent.content.secondaryCTAText}
            secondaryButtonLink={
              storyblokLinkToYocoLink(headerContent.content.secondaryCTALink) ??
              ""
            }
            isBannerContents={toastNotificationContent?.length ? true : false}
            showCartWidget={pageData?.showCartWidget}
            isBackgroundTransparent={pageData.isNavigationTransparent}
            primaryButtonTrackingEvent={headerContent.content.trackingEvent}
            primaryButtonTrackingEventKey={
              headerContent.content.trackingEventKey
            }
            primaryButtonTrackingEventValue={
              headerContent.content.trackingEventValue
            }
            secondaryButtonTrackingEvent={
              headerContent.content.secondaryCTATrackingEvent
            }
            secondaryButtonTrackingEventKey={
              headerContent.content.secondaryCTATrackingEventKey
            }
            secondaryButtonTrackingEventValue={
              headerContent.content.secondaryCTATrackingEventValue
            }
          />
        </div>
      ) : null}
      <div className="flex min-h-screen flex-col">
        <main>
          {children}

          <GetStartedModal data={getStartedData} />
        </main>

        {!pageData?.hide_footer && showFooter ? (
          <Suspense>
            <div
              className={classNames(
                "grow",
                getBackgroundColorClass(
                  pageData?.baseBackgroundColor || Color.Charcoal
                )
              )}
            >
              <Footer footer={queryResult.footer} />
            </div>
          </Suspense>
        ) : null}
      </div>
    </>
  )
}

export default Layout
